import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Collects section IDs from all cart-items-component elements on the page.
 * @returns {string} Comma-separated section IDs
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return Array.from(ids).join(',');
}

// Handle swap button clicks via event delegation
document.addEventListener('click', (event) => {
  const btn = /** @type {HTMLElement|null} */ (event.target?.closest('.cart-swap__button'));
  if (!btn) return;

  const { swapLineKey, swapVariantId } = btn.dataset;
  const quantity = parseInt(btn.dataset.swapLineQuantity ?? '1', 10);
  if (!swapLineKey || !swapVariantId || Number.isNaN(quantity)) return;

  const btnText = btn.querySelector('.btn-text');
  const originalText = btnText?.textContent ?? '';

  // The row is removed by the cart re-render on success, so this only ever runs
  // when the swap failed and the button has to go back to being pressable.
  const resetButton = () => {
    if (btnText) btnText.textContent = originalText;
    btn.removeAttribute('disabled');
    btn.removeAttribute('aria-busy');
  };

  btn.setAttribute('disabled', '');
  btn.setAttribute('aria-busy', 'true');
  if (btnText) btnText.textContent = btn.dataset.swappingText || originalText;

  // One update rather than a remove followed by an add: a single request cannot
  // leave the cart half-swapped, and there is no intermediate re-render where
  // the old line has gone but its replacement has not arrived.
  //
  // Only ONE unit is swapped; the rest of the line stays. The keys are mixed on
  // purpose — the source is addressed by line key (two lines can share a variant
  // and differ by properties or selling plan), the target by variant id, since
  // it has no line yet.
  /** @type {Record<string, number>} */
  const updates = {
    [swapLineKey]: Math.max(quantity - 1, 0),
    [swapVariantId]: 1,
  };

  /** @type {Record<string, unknown>} */
  const body = { updates };
  const sections = getCartSectionIds();
  if (sections) body.sections = sections;

  // fetchConfig, not hand-rolled headers: cart_update_url has no `.js` suffix
  // (unlike cart_add_url, which scripts.liquid appends one to), so the endpoint
  // answers with the rendered cart PAGE unless the request asks for JSON. The
  // helper sets the Accept header that makes it return JSON.
  fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(body) }))
    .then((res) => res.json())
    .then((data) => {
      if (data.status) {
        resetButton();
        return;
      }

      btn.dispatchEvent(
        new CartUpdateEvent(data, swapLineKey, {
          source: 'cart-swap',
          itemCount: data.item_count,
          variantId: swapVariantId,
          sections: data.sections,
        })
      );
    })
    .catch((err) => {
      console.error('Cart swap error:', err);
      resetButton();
    });
});
